import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";

import { useClickAway, useEventListener } from "@litres-libs/global-hooks";
import { SpriteIcon } from "@litres-libs/sprite-icons";

import { isFocusElement, KeyCodes } from "./utils";
import styles from "./Dropdown.module.scss";
import { useLocale } from "../../locale";

interface IDropdown<T> {
  items: IDropdownItem<T>[];
  defaultActiveId: T;
  onChange: (selectedId: T) => void;
  className?: string;
  menuClassName?: string;
  e2eId?: string;
  renderContentItem?: (id: T) => JSX.Element;
}

export interface IDropdownItem<T> {
  id: T;
  value: string;
}

export function Dropdown<T extends string>({
  items = [],
  defaultActiveId,
  onChange,
  className,
  menuClassName,
  renderContentItem,
  e2eId,
}: IDropdown<T>) {
  const { t } = useLocale();
  const [activeItemId, setActiveItemId] = useState(defaultActiveId);
  const [isOpen, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuItemsListNode = useRef<HTMLDivElement>(null);

  const handleDropdownClick = () => {
    setOpen(isOpen => !isOpen);
  };

  const handleMenuItemClick = (id: T) => {
    setActiveItemId(id);
    onChange(id);
    setOpen(false);
  };

  const handleKeyPress = (event: Event): void => {
    const { key } = event as KeyboardEvent;
    if (
      !isOpen ||
      ![KeyCodes.ArrowDown, KeyCodes.ArrowUp, KeyCodes.Tab].includes(
        key as KeyCodes
      )
    ) {
      return;
    }

    const menuItems = dropdownMenuItemsListNode.current
      ? Array.from(dropdownMenuItemsListNode.current.children)
      : [];

    const firstIndex = 0;
    const lastIndex = menuItems.length - 1;
    const currentIndex = menuItems.findIndex(isFocusElement);

    // запрещаем использование Tab для навигации в меню
    // при клике на Tab выбираем элемент списка в фокусе
    if (isOpen && key === KeyCodes.Tab) {
      event.preventDefault();
      handleMenuItemClick(items[currentIndex].id);
      return;
    }

    const prevIndex = isFocusElement(menuItems[firstIndex])
      ? lastIndex
      : currentIndex - 1;

    const nextIndex = isFocusElement(menuItems[lastIndex])
      ? firstIndex
      : currentIndex + 1;

    if (key === KeyCodes.ArrowDown) {
      (
        menuItems[currentIndex === -1 ? firstIndex : nextIndex] as HTMLElement
      ).focus();
    }

    if (key === KeyCodes.ArrowUp) {
      if (
        dropdownRef?.current &&
        (currentIndex === -1 || prevIndex === lastIndex)
      ) {
        dropdownRef.current.focus();
      } else {
        (
          menuItems[currentIndex === -1 ? lastIndex : prevIndex] as HTMLElement
        ).focus();
      }
    }
  };

  // эвенты переключения через стрелочки
  useEventListener("keydown", handleKeyPress);

  // закрытие меню при клике вне меню
  useClickAway(dropdownRef, () => {
    setOpen(false);
  });

  // закрываем попап при нажатии Esc
  useEventListener(
    "keydown",
    event => {
      const { key } = event as KeyboardEvent;
      if (key === KeyCodes.Escape) {
        setOpen(false);
        (dropdownRef.current?.children[0] as HTMLElement)?.focus();
      }
    },
    dropdownRef
  );

  // закрываем дропдаун при потере фокуса
  useEventListener(
    "focusout",
    (event: Event) => {
      const focusEvent = event as FocusEvent;
      const isActivedDropdownItem =
        focusEvent?.relatedTarget ===
        (dropdownRef.current?.children[0] as HTMLElement);

      const isFocusOut = !items.some(
        item => item.id === (focusEvent?.relatedTarget as Element)?.id
      );
      if (isFocusOut && !isActivedDropdownItem) {
        setOpen(false);
      }
    },
    dropdownMenuItemsListNode
  );

  useEffect(() => {
    // запрещаем прокрутку страницы при открытом окне
    document.body.style.overflow = isOpen ? "hidden" : "";

    // фокусируемся на активном элементе при открытии меню через клавиатуру
    if (isOpen) {
      const menuItems = dropdownMenuItemsListNode.current
        ? Array.from(dropdownMenuItemsListNode.current.children)
        : [];
      menuItems.forEach(item => {
        if (item.id === activeItemId) {
          (item as HTMLElement).focus();
        }
      });
    }

    return (): void => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  const title = items.find(item => item.id === activeItemId)?.value;

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <div
        className={cn(className, styles.dropdown__input, {
          [styles.dropdown__input_open]: isOpen,
        })}
        onClick={handleDropdownClick}
        data-testid={e2eId}
        onKeyDown={e => {
          if (e.code === KeyCodes.Enter || e.code === KeyCodes.Space) {
            e.preventDefault();
            handleDropdownClick();
          }
        }}
        tabIndex={0}
        role="menu"
        aria-label={`${title}, ${t("Выбрать из списка")} (${isOpen ? t("развернуто") : t("свернуто")})`}
      >
        <span
          className={styles.dropdown__input__selected}
          data-testid="sorting__selectedItem"
        >
          {title}
        </span>
        <SpriteIcon
          name={isOpen ? "chevronUp" : "chevronDown"}
          className={styles.dropdown__icon}
          size={24}
        />
      </div>
      {isOpen && (
        <div
          className={cn(styles.dropdown__menu, menuClassName)}
          role="listbox"
          id="dropdownMenu"
          aria-activedescendant={activeItemId}
          ref={dropdownMenuItemsListNode}
        >
          {items.map(({ id, value }) => (
            <div
              onClick={() => {
                handleMenuItemClick(id);
              }}
              onKeyDown={e => {
                if (e.code === KeyCodes.Enter || e.code === KeyCodes.Space) {
                  e.preventDefault();
                  (dropdownRef.current?.children[0] as HTMLElement)?.focus();
                  handleMenuItemClick(id);
                }
              }}
              id={id}
              role="option"
              aria-selected={id === activeItemId}
              aria-label={value}
              tabIndex={-1}
              className={styles.dropdown__menu__item}
              key={id}
              data-testid={`sorting__dropDownItem--${id}`}
            >
              {renderContentItem ? renderContentItem(id) : value}

              {id === activeItemId && (
                <SpriteIcon
                  name="checkbox"
                  size={16}
                  className={styles.dropdown__menu__icon}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
